import { createRoute, Link, useParams } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Copy, Truck } from "lucide-react";

import { rootRoute } from "./root.tsx";
import { useIsDesktop } from "../lib/viewport.ts";
import { DesktopOrderDetail } from "./desktop/order-detail.tsx";
import { SubPage } from "../components/layout.tsx";
import { Button, ErrorBlock, Img, OrderStatus, Price, Spinner } from "../components/ui.tsx";
import { api } from "../api/client.ts";

/**
 * 订单详情。
 *
 * 🔴 **之前根本没有这一页** —— 供应商填了快递单号，买家却没有任何地方能看到。
 *    建材是大件配送，用户要等货、要跟司机对接，看不到快递单号等于让他干等。
 *
 * ⚠️ 时间线是服务端按白名单裁剪过的：只出"发生了什么 + 什么时候"，
 *    不带 operator 之类的内部字段。
 */

interface OrderDetail {
  id: number;
  orderNo: string;
  payment: string;
  fulfillment: string;
  goodsAmount: number;
  freightAmount: number;
  discountAmount: number;
  payableAmount: number;
  paidAmount: number;
  refundedAmount: number;
  shipName: string;
  shipPhone: string;
  shipAddress: Record<string, string>;
  buyerNote: string;
  shippingCarrier: string | null;
  trackingNo: string | null;
  createdAt: string;
  items: {
    id: number;
    titleSnapshot: string;
    specsSnapshot: Record<string, string> | null;
    /** ⚠️ 服务端已把存储 key 转成地址，前端不用管 key 怎么拼。 */
    imageUrl: string | null;
    unitPrice: number;
    quantity: number;
  }[];
  timeline: { event: string; label: string; at: string }[];
  refunds: { refundNo: string; amount: number; status: string; reason: string; createdAt: string }[];
}

const REFUND_STATUS: Record<string, string> = {
  pending: "待审核",
  done: "已退款",
  rejected: "已驳回",
};

function OrderDetailPage() {
  const { id } = useParams({ from: "/orders/$id" });
  const { data, isPending, error, refetch } = useQuery({
    queryKey: ["order", id],
    queryFn: () => api.get<OrderDetail>(`/orders/${id}`),
  });

  if (isPending) {
    return (
      <SubPage title="订单详情">
        <Spinner />
      </SubPage>
    );
  }
  if (error) {
    return (
      <SubPage title="订单详情">
        <ErrorBlock error={error} onRetry={() => refetch()} />
      </SubPage>
    );
  }

  const addr = data.shipAddress ?? {};

  return (
    <SubPage title="订单详情">
      <section className="bg-white px-4 py-3">
        <div className="flex items-center justify-between">
          <span className="font-mono text-xs text-ink-500">{data.orderNo}</span>
          <OrderStatus payment={data.payment as never} fulfillment={data.fulfillment as never} />
        </div>
      </section>

      {/* ── 物流 ── */}
      {/* 🔴 这一段就是这一页存在的理由：之前单号填了但买家看不到 */}
      {data.trackingNo && (
        <section className="mt-2 bg-white px-4 py-3">
          <h2 className="mb-2 flex items-center gap-1.5 text-sm font-medium">
            <Truck className="size-4 text-ink-500" />
            物流
          </h2>
          <div className="flex items-center gap-3">
            <div className="min-w-0 flex-1 text-sm">
              <p>{data.shippingCarrier || "承运商"}</p>
              <p className="mt-0.5 font-mono text-xs text-ink-500">{data.trackingNo}</p>
            </div>
            {/*
              ⚠️ 建材配送常要把单号发给师傅或物业，复制按钮比让人手抄靠谱。
                 clipboard 在非安全上下文里不可用，失败就静默 —— 用户还能手选。
            */}
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                void navigator.clipboard?.writeText(data.trackingNo!).catch(() => {});
              }}
            >
              <Copy className="size-3.5" />
              复制
            </Button>
          </div>
        </section>
      )}

      {/* ── 商品 ── */}
      <section className="mt-2 bg-white">
        {data.items.map((it) => (
          <div key={it.id} className="flex gap-3 border-b border-neutral-100 px-4 py-3 last:border-0">
            <Img src={it.imageUrl} alt="" className="size-16 shrink-0 rounded object-cover" />
            <div className="min-w-0 flex-1">
              <p className="line-clamp-2 text-sm leading-snug">{it.titleSnapshot}</p>
              <p className="mt-1 text-xs text-ink-500">
                {Object.values(it.specsSnapshot ?? {}).join(" · ")}
              </p>
            </div>
            <div className="shrink-0 text-right text-sm">
              <Price cents={it.unitPrice} />
              <p className="text-xs text-ink-500">×{it.quantity}</p>
            </div>
          </div>
        ))}
      </section>

      {/* ── 金额 ── */}
      <section className="mt-2 bg-white px-4 py-3 text-sm">
        <div className="flex justify-between py-1">
          <span className="text-ink-500">商品</span>
          <Price cents={data.goodsAmount} />
        </div>
        <div className="flex justify-between py-1">
          <span className="text-ink-500">运费</span>
          <Price cents={data.freightAmount} />
        </div>
        {data.discountAmount > 0 && (
          <div className="flex justify-between py-1">
            <span className="text-ink-500">优惠</span>
            <span className="text-brand-600">
              −<Price cents={data.discountAmount} />
            </span>
          </div>
        )}
        <div className="flex justify-between border-t border-neutral-100 pt-2 font-medium">
          <span>实付</span>
          <Price cents={data.payableAmount} className="text-brand-600" />
        </div>
        {data.refundedAmount > 0 && (
          <div className="flex justify-between pt-1 text-xs">
            <span className="text-ink-500">已退款</span>
            <Price cents={data.refundedAmount} />
          </div>
        )}
      </section>

      {/* ── 收货信息 ── */}
      <section className="mt-2 bg-white px-4 py-3">
        <h2 className="mb-1.5 text-sm font-medium">收货信息</h2>
        <p className="text-sm">
          {data.shipName} {data.shipPhone}
        </p>
        <p className="mt-0.5 text-sm text-ink-500">
          {addr.province}
          {addr.city}
          {addr.district} {addr.detail}
        </p>
        {data.buyerNote && (
          <p className="mt-1.5 text-xs text-ink-500">备注：{data.buyerNote}</p>
        )}
      </section>

      {/* ── 退款记录 ── */}
      {data.refunds.length > 0 && (
        <section className="mt-2 bg-white px-4 py-3">
          <h2 className="mb-2 text-sm font-medium">退款记录</h2>
          {data.refunds.map((r) => (
            <div key={r.refundNo} className="border-t border-neutral-100 py-2 first:border-0 first:pt-0">
              <div className="flex justify-between text-sm">
                <span className="font-mono text-xs text-ink-500">{r.refundNo}</span>
                <span>
                  <Price cents={r.amount} /> · {REFUND_STATUS[r.status] ?? r.status}
                </span>
              </div>
              {r.reason && <p className="mt-0.5 text-xs text-ink-500">{r.reason}</p>}
            </div>
          ))}
        </section>
      )}

      {/* ── 时间线 ── */}
      <section className="mt-2 bg-white px-4 py-3">
        <h2 className="mb-2 text-sm font-medium">订单进度</h2>
        <ol className="space-y-2">
          {data.timeline.map((t, i) => (
            <li key={`${t.event}-${i}`} className="flex gap-3 text-sm">
              <span className="mt-1.5 size-1.5 shrink-0 rounded-full bg-brand-600" />
              <span className="flex-1">{t.label}</span>
              <span className="shrink-0 text-xs text-ink-500">
                {new Date(t.at).toLocaleString("zh-CN")}
              </span>
            </li>
          ))}
        </ol>
      </section>

      <div className="px-4 py-4">
        <Link to="/orders" search={{}}>
          <Button variant="outline" className="w-full">
            返回订单列表
          </Button>
        </Link>
      </div>
    </SubPage>
  );
}

/** 手机版 / 网页版是两套页面，按视口挑一套（URL 与数据 hooks 两边共用）。 */
function OrderDetailRoute() {
  return useIsDesktop() ? <DesktopOrderDetail /> : <OrderDetailPage />;
}

export const orderDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/orders/$id",
  component: OrderDetailRoute,
});
